import React, { useState, useEffect, useMemo, useCallback, useRef, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import api, { authService } from '../api/axios';
import ProductCard from '../components/ProductCard';
import AddProductModal from '../components/AddProductModal';
import ReactSlider from 'react-slider';
import CategoryManager from '../components/CategoryManager';
import { debounce } from 'lodash';
import ProductsPage from './ProductsPage';
import './ProductsPage.css';
import './AdminPage.css';
import CreateCuratorForm from '../components/CreateCuratorForm';
import BrandManager from '../components/BrandManager';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import QuoteCartPage from './QuoteCartPage';
import TeamChatAdmin from '../components/TeamChatAdmin';
import AnalyticsReportsManagement from '../components/AnalyticsReportsManagement';
import BrandstoreManagement from '../components/BrandstoreManagement';
import GalleryManagement from '../components/GalleryManagement';
import CatalogImportManagement from '../components/catalog-import/CatalogImportManagement';
import VisitBookingsManagement from '../components/VisitBookingsManagement';
import LeadsManagement from '../components/LeadsManagement';
import CartAnalytics from '../components/CartAnalytics';
import { lostReasonLabel, lostReasonGroup } from '../constants/lostReasons';
import { isStalling } from '../constants/cartAging';
import UserManagement from '../components/UserManagement';
import { useToast } from '../context/ToastContext';
import { adminViewFromParam, featureGroupsForRole } from '../config/features';
import useFeatureBadges from '../hooks/useFeatureBadges';

function AdminSidebar({ currentView, onNavigate, user, badges, collapsed, onToggle }) {
    const role = user?.role || '';
    const initials = user?.email ? user.email.slice(0, 2).toUpperCase() : 'AD';

    // Sidebar shows only the features that live inside /admin. The same
    // registry also drives the dashboard, so a new feature surfaces in both.
    const navGroups = featureGroupsForRole(role, { adminViewsOnly: true });

    // Tooltip is positioned in JS rather than with CSS because .admin-sidebar
    // sets overflow:hidden, which would clip an absolutely-positioned bubble.
    // Anchoring it fixed to the hovered row's rect escapes that.
    const [tip, setTip] = useState(null);

    const showTip = (event, text) => {
        const rect = event.currentTarget.getBoundingClientRect();
        setTip({ text, top: rect.top + rect.height / 2, left: rect.right + 10 });
    };
    const hideTip = () => setTip(null);

    return (
        <>
        <aside className={`admin-sidebar${collapsed ? ' admin-sidebar--collapsed' : ''}`}>
            <div className="admin-sidebar__brand">
                {!collapsed && <span className="admin-sidebar__brand-text">Menu</span>}
                <button className="admin-sidebar__toggle" onClick={onToggle} title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
                    {collapsed ? '›' : '‹'}
                </button>
            </div>

            <Link
                to="/dashboard"
                className="admin-sidebar__back"
                title={collapsed ? 'Back to dashboard' : undefined}
            >
                <span className="admin-sidebar__back-arrow" aria-hidden="true">←</span>
                {!collapsed && <span>Dashboard</span>}
            </Link>

            <nav className="admin-sidebar__nav">
                {navGroups.map(({ group, items }) => {
                    const inIframe = window.self !== window.top;
                    const visible = items.filter(item => !(inIframe && item.key === 'Employee'));
                    if (visible.length === 0) return null;
                    return (
                        <div key={group} className="admin-sidebar__group">
                            {!collapsed && <div className="admin-sidebar__group-label">{group}</div>}
                            {visible.map(item => {
                                const isActive = currentView === item.key;
                                const count = item.badgeKey ? (badges[item.badgeKey] || 0) : 0;
                                return (
                                    <button
                                        key={item.key}
                                        className={`admin-sidebar__item${isActive ? ' admin-sidebar__item--active' : ''}`}
                                        onClick={() => onNavigate(item.key)}
                                        // Carry the registry's description across so the sidebar
                                        // teaches what the dashboard cards do, not just labels.
                                        // Focus handlers included so keyboard users get it too.
                                        onMouseEnter={e => showTip(e, collapsed
                                            ? `${item.label} — ${item.description}`
                                            : item.description)}
                                        onMouseLeave={hideTip}
                                        onFocus={e => showTip(e, collapsed
                                            ? `${item.label} — ${item.description}`
                                            : item.description)}
                                        onBlur={hideTip}
                                        aria-label={collapsed
                                            ? `${item.label} — ${item.description}`
                                            : undefined}
                                    >
                                        <span className="admin-sidebar__icon">{item.icon}</span>
                                        {!collapsed && <span className="admin-sidebar__label">{item.label}</span>}
                                        {count > 0 && (
                                            <span className={`admin-sidebar__badge${collapsed ? ' admin-sidebar__badge--dot' : ''}`}>
                                                {collapsed ? '' : count}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    );
                })}
            </nav>

            <div className="admin-sidebar__footer">
                <div className="admin-sidebar__avatar">{initials}</div>
                {!collapsed && (
                    <div className="admin-sidebar__user">
                        <div className="admin-sidebar__user-email">{user?.email}</div>
                        <div className="admin-sidebar__user-role">{role}</div>
                    </div>
                )}
            </div>
        </aside>

        {/* Sibling of the sidebar, not a child: the sidebar clips its overflow. */}
        {tip && (
            <div
                className="admin-tooltip"
                role="tooltip"
                style={{ top: tip.top, left: tip.left }}
            >
                {tip.text}
            </div>
        )}
        </>
    );
}

// ── Inline Curated Carts List component ──────────────────────────────
const PAGE_SIZE = 10;

const QuotesList = ({ onCreateNew, onResumeDraft }) => {
    const { addToast } = useToast();
    const [quotes, setQuotes] = React.useState([]);
    const [isLoading, setIsLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [copiedId, setCopiedId] = React.useState(null);
    const [activeFilter, setActiveFilter] = React.useState('ALL');
    const [searchTerm, setSearchTerm] = React.useState('');
    const [dateFrom, setDateFrom] = React.useState('');
    const [dateTo, setDateTo] = React.useState('');
    const [createdByFilter, setCreatedByFilter] = React.useState('');
    const [assignedScmFilter, setAssignedScmFilter] = React.useState('');
    const [currentPage, setCurrentPage] = React.useState(1);
    const [drafts, setDrafts] = React.useState([]);
    const [isDraftsLoading, setIsDraftsLoading] = React.useState(false);

    const navigate = useNavigate();

    React.useEffect(() => {
        api.get('/quotes/all')
            .then(r => { setQuotes(r.data || []); setIsLoading(false); })
            .catch(() => { setError('Could not load curated carts.'); setIsLoading(false); });

        setIsDraftsLoading(true);
        api.get('/quotes/drafts')
            .then(r => { setDrafts(r.data || []); setIsDraftsLoading(false); })
            .catch(() => { setIsDraftsLoading(false); });
    }, []);

    const fmt = (n) => n != null
        ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n)
        : '—';

    const dateFilteredQuotes = React.useMemo(() => quotes.filter(q => {
        if (!q.createdAt) return true;
        const d = new Date(q.createdAt);
        if (dateFrom && d < new Date(dateFrom)) return false;
        if (dateTo && d > new Date(dateTo + 'T23:59:59')) return false;
        return true;
    }), [quotes, dateFrom, dateTo]);

    const createdByOptions = React.useMemo(() =>
        [...new Set(quotes.map(q => q.createdByEmail).filter(Boolean))].sort(), [quotes]);
    const assignedScmOptions = React.useMemo(() =>
        [...new Set(quotes.map(q => q.assignedScmEmail).filter(Boolean))].sort(), [quotes]);

    // Feeds both the table and the analytics stat cards — date range + Created By/Assigned By
    // narrow the "universe"; status/search filters below only slice the already-computed stats.
    const fullyFilteredForAnalytics = React.useMemo(() => {
        let result = dateFilteredQuotes;
        if (createdByFilter) {
            result = createdByFilter === '__UNASSIGNED__'
                ? result.filter(q => !q.createdByEmail)
                : result.filter(q => q.createdByEmail === createdByFilter);
        }
        if (assignedScmFilter) {
            result = assignedScmFilter === '__UNASSIGNED__'
                ? result.filter(q => !q.assignedScmEmail)
                : result.filter(q => q.assignedScmEmail === assignedScmFilter);
        }
        return result;
    }, [dateFilteredQuotes, createdByFilter, assignedScmFilter]);

    // --- ANALYTICS ENGINE ---
    const analytics = React.useMemo(() => {
        const stats = {
            total: fullyFilteredForAnalytics.length,
            totalValue: 0,
            requested: 0, requestedValue: 0,
            cart: 0,      cartValue: 0,
            quote: 0,     quoteValue: 0,
            hold: 0,      holdValue: 0,
            converted: 0, convertedValue: 0,
            closedLost: 0, closedLostValue: 0,
            lostReasons: {},
            rfqsConverted: 0,
            abandoned: 0, abandonedValue: 0,
            totalRfqs: 0,
        };

        const now = new Date();

        fullyFilteredForAnalytics.forEach(q => {
            const val = q.totalValue || 0;
            stats.totalValue += val;

            if (q.status === 'REQUESTED') { stats.requested++; stats.requestedValue += val; }
            if (q.status === 'CART')      { stats.cart++;      stats.cartValue      += val; }
            if (q.status === 'QUOTE')     { stats.quote++;     stats.quoteValue     += val; }
            if (q.status === 'HOLD')      { stats.hold++;      stats.holdValue      += val; }
            if (q.status === 'CONVERTED') { stats.converted++; stats.convertedValue += val; }
            if (q.status === 'CLOSED_LOST') {
                stats.closedLost++;
                stats.closedLostValue += val;
                // Carts closed before reasons were captured group under '' and are
                // reported as "not recorded" rather than being dropped, so the
                // breakdown always reconciles with the Closed Lost card.
                const key = q.lostReason || '';
                const bucket = stats.lostReasons[key] || { count: 0, value: 0 };
                bucket.count++;
                bucket.value += val;
                stats.lostReasons[key] = bucket;
            }

            const isRfq = q.cartName.startsWith('RFQ-');
            if (isRfq) {
                stats.totalRfqs++;
                if (q.status === 'QUOTE' || q.status === 'CONVERTED') stats.rfqsConverted++;
            }

            // Ages every non-terminal stage against its own threshold, not just
            // CART and REQUESTED — see constants/cartAging.js. QUOTE and HOLD hold
            // the most value and were previously never checked.
            if (isStalling(q, now.getTime())) {
                stats.abandoned++;
                stats.abandonedValue += val;
            }
        });

        return stats;
    }, [fullyFilteredForAnalytics]);

    // Sort latest first, then filter by status card, then filter by search term
    const filteredQuotes = React.useMemo(() => {
        const now = new Date();

        // 1. Sort: latest createdAt first
        const sorted = [...fullyFilteredForAnalytics].sort((a, b) =>
            new Date(b.createdAt) - new Date(a.createdAt)
        );

        // 2. Status filter
        let result = sorted;
        if (activeFilter === 'ABANDONED') {
            // Same predicate as the card above, so clicking it can never disagree
            // with the number it showed.
            result = sorted.filter(q => isStalling(q, now.getTime()));
        } else if (activeFilter !== 'ALL') {
            result = sorted.filter(q => q.status === activeFilter);
        }

        // 3. Search filter across cart name, created by, status
        if (searchTerm.trim()) {
            const q = searchTerm.toLowerCase();
            result = result.filter(c =>
                c.cartName?.toLowerCase().includes(q) ||
                c.createdByEmail?.toLowerCase().includes(q) ||
                c.status?.toLowerCase().includes(q) ||
                c.quoteCreatedFor?.toLowerCase().includes(q)
            );
        }

        return result;
    }, [fullyFilteredForAnalytics, activeFilter, searchTerm]);

    // Pagination derived values
    const totalPages = Math.max(1, Math.ceil(filteredQuotes.length / PAGE_SIZE));
    const pagedQuotes = filteredQuotes.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    // Reset to page 1 whenever filter or search changes
    React.useEffect(() => { setCurrentPage(1); }, [activeFilter, searchTerm, createdByFilter, assignedScmFilter]);

    const handleFilterClick = (filterType) => {
        setActiveFilter(prev => prev === filterType ? 'ALL' : filterType);
    };

    const handleCopy = (cartName, id) => {
        const link = `${window.location.origin}/#/quote?name=${encodeURIComponent(cartName)}`;

        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(link)
                .then(() => {
                    setCopiedId(id);
                    setTimeout(() => setCopiedId(null), 2000);
                })
                .catch(err => {
                    console.error('Failed to copy:', err);
                    addToast('Clipboard access denied by the browser.', 'error');
                });
        } else {
            const textArea = document.createElement("textarea");
            textArea.value = link;
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            try {
                document.execCommand('copy');
                setCopiedId(id);
                setTimeout(() => setCopiedId(null), 2000);
            } catch (err) {
                console.error('Fallback copy failed', err);
            }
            document.body.removeChild(textArea);
        }
    };

    if (isLoading) return <div className="loading-state">Loading carts…</div>;

    return (
        <div className="ql-wrapper">
            <div className="ql-header">
                <div>
                    <div className="ql-eyebrow">Cart Management</div>
                    <h2 className="ql-title">Curated Carts & Analytics</h2>
                </div>
                <button className="add-product-button" onClick={onCreateNew}>+ Create New Cart</button>
            </div>

            {/* ── Staging Area — DB-backed drafts ── */}
            <div style={{ marginBottom: '1.5rem' }}>
                <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#6b7280', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
                    Staging Area
                </div>
                {isDraftsLoading ? (
                    <div style={{
                        background: '#f9fafb', border: '1.5px dashed #d1d5db',
                        borderRadius: '10px', padding: '0.875rem 1.25rem',
                        fontSize: '0.875rem', color: '#9ca3af',
                    }}>
                        Loading drafts…
                    </div>
                ) : drafts.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {drafts.map(draft => (
                            <div key={draft.id} style={{
                                display: 'flex', alignItems: 'center', gap: '1rem',
                                background: '#fff7ed', border: '1.5px dashed #fb923c',
                                borderRadius: '10px', padding: '0.875rem 1.25rem', flexWrap: 'wrap',
                            }}>
                                <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: '#fed7aa', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', flexShrink: 0 }}>🗂️</div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: 600, color: '#c2410c', fontSize: '0.9rem' }}>
                                        {draft.quoteCreatedFor || 'No client name yet'}
                                    </div>
                                    <div style={{ fontSize: '0.8rem', color: '#ea580c', marginTop: '2px' }}>
                                        {draft.itemCount > 0
                                            ? <span>{draft.itemCount} item{draft.itemCount !== 1 ? 's' : ''}</span>
                                            : <span>No items yet</span>}
                                        <span style={{ marginLeft: '8px', opacity: 0.75 }}>
                                            · Updated {draft.updatedAt || draft.createdAt
                                                ? new Date(draft.updatedAt || draft.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                                                : '—'}
                                        </span>
                                    </div>
                                </div>
                                <button
                                    onClick={() => onResumeDraft(draft.id)}
                                    style={{ background: '#f97316', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.45rem 1rem', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer', flexShrink: 0 }}
                                >
                                    Resume
                                </button>
                                <button
                                    onClick={() => {
                                        api.delete(`/quotes/drafts/${draft.id}`)
                                            .then(() => setDrafts(prev => prev.filter(d => d.id !== draft.id)))
                                            .catch(() => {});
                                    }}
                                    style={{ background: 'transparent', color: '#c2410c', border: '1px solid #fb923c', borderRadius: '6px', padding: '0.45rem 1rem', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer', flexShrink: 0 }}
                                >
                                    Delete
                                </button>
                            </div>
                        ))}
                        <button onClick={onCreateNew} style={{ alignSelf: 'flex-start', background: 'transparent', color: '#374151', border: '1.5px solid #d1d5db', borderRadius: '6px', padding: '0.45rem 1rem', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer' }}>
                            + Start New Cart
                        </button>
                    </div>
                ) : (
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: '1rem',
                        background: '#f9fafb', border: '1.5px dashed #d1d5db',
                        borderRadius: '10px', padding: '0.875rem 1.25rem',
                    }}>
                        <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', flexShrink: 0 }}>📋</div>
                        <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 500, color: '#6b7280', fontSize: '0.875rem' }}>No drafts in staging</div>
                            <div style={{ fontSize: '0.78rem', color: '#9ca3af', marginTop: '2px' }}>Save a draft while creating a cart and it will appear here.</div>
                        </div>
                        <button onClick={onCreateNew} style={{ background: 'transparent', color: '#374151', border: '1.5px solid #d1d5db', borderRadius: '6px', padding: '0.45rem 1rem', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer', flexShrink: 0 }}>
                            + Start New Cart
                        </button>
                    </div>
                )}
            </div>

            {!error && quotes.length > 0 && (
                <div className="analytics-dashboard">
                    <div
                        className={`stat-card clickable ${activeFilter === 'ALL' ? 'active-filter border-blue' : ''}`}
                        onClick={() => setActiveFilter('ALL')}
                    >
                        <div className="stat-value text-blue">{analytics.total}</div>
                        <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#2563eb', opacity: 0.8, marginTop: '2px' }}>{fmt(analytics.totalValue)}</div>
                        <div className="stat-label">Total Workspaces</div>
                    </div>
                    <div
                        className={`stat-card clickable ${activeFilter === 'REQUESTED' ? 'active-filter border-orange' : ''}`}
                        onClick={() => handleFilterClick('REQUESTED')}
                    >
                        <div className="stat-value text-orange">{analytics.requested}</div>
                        <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#f59e0b', opacity: 0.8, marginTop: '2px' }}>{fmt(analytics.requestedValue)}</div>
                        <div className="stat-label">Pending RFQs</div>
                    </div>
                    <div
                        className={`stat-card clickable ${activeFilter === 'CART' ? 'active-filter border-purple' : ''}`}
                        onClick={() => handleFilterClick('CART')}
                    >
                        <div className="stat-value text-purple">{analytics.cart}</div>
                        <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#7c3aed', opacity: 0.8, marginTop: '2px' }}>{fmt(analytics.cartValue)}</div>
                        <div className="stat-label">In Cart Stage</div>
                    </div>
                    <div
                        className={`stat-card clickable ${activeFilter === 'QUOTE' ? 'active-filter border-indigo' : ''}`}
                        onClick={() => handleFilterClick('QUOTE')}
                    >
                        <div className="stat-value text-indigo">{analytics.quote}</div>
                        <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#4f46e5', opacity: 0.8, marginTop: '2px' }}>{fmt(analytics.quoteValue)}</div>
                        <div className="stat-label">Quotes Sent</div>
                    </div>
                    <div
                        className={`stat-card clickable ${activeFilter === 'HOLD' ? 'active-filter border-amber' : ''}`}
                        onClick={() => handleFilterClick('HOLD')}
                    >
                        <div className="stat-value text-amber">{analytics.hold}</div>
                        <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#92610a', opacity: 0.8, marginTop: '2px' }}>{fmt(analytics.holdValue)}</div>
                        <div className="stat-label">On Hold</div>
                    </div>
                    <div
                        className={`stat-card clickable ${activeFilter === 'CONVERTED' ? 'active-filter border-green' : ''}`}
                        onClick={() => handleFilterClick('CONVERTED')}
                    >
                        <div className="stat-value text-green">{analytics.converted}</div>
                        <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#16a34a', opacity: 0.8, marginTop: '2px' }}>{fmt(analytics.convertedValue)}</div>
                        <div className="stat-label">Successful Conversions</div>
                    </div>
                    <div
                        className={`stat-card clickable ${activeFilter === 'CLOSED_LOST' ? 'active-filter border-slate' : ''}`}
                        onClick={() => handleFilterClick('CLOSED_LOST')}
                    >
                        <div className="stat-value text-slate">{analytics.closedLost}</div>
                        <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#475569', opacity: 0.8, marginTop: '2px' }}>{fmt(analytics.closedLostValue)}</div>
                        <div className="stat-label">Closed Lost</div>
                    </div>
                    <div
                        className={`stat-card clickable ${activeFilter === 'ABANDONED' ? 'active-filter border-red' : ''}`}
                        onClick={() => handleFilterClick('ABANDONED')}
                    >
                        <div className="stat-value text-red">{analytics.abandoned}</div>
                        <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#dc2626', opacity: 0.8, marginTop: '2px' }}>{fmt(analytics.abandonedValue)}</div>
                        {/* No longer a single "14+ days": each stage is aged against its
                            own threshold, so the label states the rule rather than a number
                            that would be wrong for three of the four stages. */}
                        <div className="stat-label" title="Requested, cart and quote stages after 14 days; on-hold after 30">
                            Stalling
                        </div>
                    </div>

                    <div className="stat-card highlight-card">
                        <div className="stat-row">
                            <span>Total RFQs Received:</span>
                            <strong>{analytics.totalRfqs}</strong>
                        </div>
                        <div className="stat-row">
                            <span>RFQs Converted to Quote:</span>
                            <strong>{analytics.rfqsConverted}</strong>
                        </div>
                        <div className="stat-progress">
                            <div
                                className="stat-progress-bar"
                                style={{ width: `${analytics.totalRfqs > 0 ? (analytics.rfqsConverted / analytics.totalRfqs) * 100 : 0}%`}}
                            ></div>
                        </div>
                    </div>
                </div>
            )}

            {/* Why we lost. Shown under the Closed Lost filter rather than always, so it
                appears where someone is already asking the question. */}
            {!error && activeFilter === 'CLOSED_LOST' && analytics.closedLost > 0 && (() => {
                // Roll the per-reason tallies up to their group. The group subtotal is the
                // number worth having — "how much did we lose to ourselves this quarter" —
                // and nobody should have to add seven rows up by eye to get it.
                const groups = {};
                Object.entries(analytics.lostReasons).forEach(([key, tally]) => {
                    // Unrecorded reasons get their own group rather than falling into
                    // 'Other', which would read as a choice someone actually made.
                    const name = key ? lostReasonGroup(key) : 'Not recorded';
                    const g = groups[name] || { count: 0, value: 0, rows: [] };
                    g.count += tally.count;
                    g.value += tally.value;
                    g.rows.push([key, tally]);
                    groups[name] = g;
                });

                return (
                    <div className="lost-reason-breakdown">
                        <div className="lost-reason-breakdown__title">Why these were lost</div>
                        {Object.entries(groups)
                            .sort((a, b) => b[1].count - a[1].count)
                            .map(([name, group]) => (
                                <div key={name} className="lost-reason-group">
                                    <div className="lost-reason-group__head">
                                        <span className={`lost-reason-group__name${name === 'Not recorded' ? ' lost-reason-group__name--empty' : ''}`}>
                                            {name}
                                        </span>
                                        <span className="lost-reason-group__total">
                                            {group.count} · {fmt(group.value)} · {Math.round((group.count / analytics.closedLost) * 100)}%
                                        </span>
                                    </div>
                                    <div className="lost-reason-breakdown__rows">
                                        {group.rows
                                            .sort((a, b) => b[1].count - a[1].count)
                                            .map(([key, { count, value }]) => (
                                                <div key={key || 'UNRECORDED'} className="lost-reason-row">
                                                    <span className={`lost-reason-row__label${key ? '' : ' lost-reason-row__label--empty'}`}>
                                                        {key ? lostReasonLabel(key) : 'No reason recorded'}
                                                    </span>
                                                    <span className="lost-reason-row__bar">
                                                        {/* Width is a share of all losses, not of the group,
                                                            so bars stay comparable across groups. */}
                                                        <span
                                                            className="lost-reason-row__fill"
                                                            style={{ width: `${Math.round((count / analytics.closedLost) * 100)}%` }}
                                                        />
                                                    </span>
                                                    <span className="lost-reason-row__count">{count} · {fmt(value)}</span>
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            ))}
                    </div>
                );
            })()}

            {error && (
                <div className="ql-error">
                    <span>⚠ {error}</span>
                    <button className="add-product-button" onClick={onCreateNew} style={{marginTop:'1rem'}}>
                        + Create New Cart instead
                    </button>
                </div>
            )}

            {!error && quotes.length === 0 && (
                <div className="ql-empty">
                    <div className="ql-empty__icon">📋</div>
                    <div className="ql-empty__msg">No curated carts yet.</div>
                    <button className="add-product-button" onClick={onCreateNew}>Create your first cart →</button>
                </div>
            )}

            {!error && quotes.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.875rem', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#6b7280' }}>Date Range:</span>
                    <input
                        type="date"
                        value={dateFrom}
                        onChange={e => setDateFrom(e.target.value)}
                        style={{ padding: '0.4rem 0.6rem', border: '1.5px solid #e5e7eb', borderRadius: '6px', fontSize: '0.82rem' }}
                    />
                    <span style={{ fontSize: '0.78rem', color: '#9ca3af' }}>to</span>
                    <input
                        type="date"
                        value={dateTo}
                        onChange={e => setDateTo(e.target.value)}
                        style={{ padding: '0.4rem 0.6rem', border: '1.5px solid #e5e7eb', borderRadius: '6px', fontSize: '0.82rem' }}
                    />
                    {(dateFrom || dateTo) && (
                        <button
                            onClick={() => { setDateFrom(''); setDateTo(''); }}
                            style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: '0.78rem', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
                        >
                            Clear
                        </button>
                    )}

                    <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#6b7280', marginLeft: '0.5rem' }}>Created By:</span>
                    <select
                        value={createdByFilter}
                        onChange={e => setCreatedByFilter(e.target.value)}
                        style={{ padding: '0.4rem 0.6rem', border: '1.5px solid #e5e7eb', borderRadius: '6px', fontSize: '0.82rem' }}
                    >
                        <option value="">All</option>
                        {createdByOptions.map(email => <option key={email} value={email}>{email}</option>)}
                    </select>

                    <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#6b7280', marginLeft: '0.5rem' }}>Assigned By:</span>
                    <select
                        value={assignedScmFilter}
                        onChange={e => setAssignedScmFilter(e.target.value)}
                        style={{ padding: '0.4rem 0.6rem', border: '1.5px solid #e5e7eb', borderRadius: '6px', fontSize: '0.82rem' }}
                    >
                        <option value="">All</option>
                        <option value="__UNASSIGNED__">Unassigned</option>
                        {assignedScmOptions.map(email => <option key={email} value={email}>{email}</option>)}
                    </select>

                    {(createdByFilter || assignedScmFilter) && (
                        <button
                            onClick={() => { setCreatedByFilter(''); setAssignedScmFilter(''); }}
                            style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: '0.78rem', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
                        >
                            Clear
                        </button>
                    )}
                </div>
            )}

            {!error && quotes.length > 0 && (
                <div className="ql-toolbar">
                    <div className="ql-search-wrap">
                        <span className="ql-search-icon">🔍</span>
                        <input
                            className="ql-search-input"
                            type="text"
                            placeholder="Search by cart name, email, client, status…"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                        {searchTerm && (
                            <button className="ql-search-clear" onClick={() => setSearchTerm('')}>✕</button>
                        )}
                    </div>
                    <div className="ql-results-count">
                        {filteredQuotes.length} result{filteredQuotes.length !== 1 ? 's' : ''}
                        {activeFilter !== 'ALL' && <> · <span style={{color:'var(--primary-color)'}}>{activeFilter}</span></>}
                    </div>
                </div>
            )}

            {!error && quotes.length > 0 && (
                <div className="ql-table-wrap">
                    <table className="ql-table">
                        <thead>
                            <tr>
                                <th>Cart Name</th>
                                <th>Prepared For</th>
                                <th>Status</th>
                                <th>Created By</th>
                                <th>Items</th>
                                <th>Total Value</th>
                                <th>Created At</th>
                                <th>Viewers</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pagedQuotes.length > 0 ? (
                                pagedQuotes.map(q => (
                                    <tr key={q.id}>
                                        <td className="ql-td-name">
                                            <div>{q.cartName}</div>
                                        </td>
                                        <td style={{ fontSize: '0.875rem', color: '#374151' }}>
                                            {q.quoteCreatedFor || <span style={{ color: '#9ca3af' }}>—</span>}
                                        </td>
                                        <td>
                                            <span className={`status-badge status-${q.status.toLowerCase()}`}>
                                                {q.status}
                                            </span>
                                            {q.status === 'CLOSED_LOST' && (
                                                <div
                                                    style={{ marginTop: '4px', fontSize: '0.72rem', color: '#6b7280', lineHeight: 1.35 }}
                                                    title={q.lostReasonNote || undefined}
                                                >
                                                    {q.lostReason
                                                        ? lostReasonLabel(q.lostReason)
                                                        // Every cart closed before this was instrumented has no reason,
                                                        // so say that rather than leaving the cell ambiguously blank.
                                                        : <span style={{ fontStyle: 'italic', color: '#9ca3af' }}>no reason recorded</span>}
                                                </div>
                                            )}
                                        </td>
                                        <td className="ql-td-email">{q.createdByEmail}</td>
                                        <td>{q.items?.length ?? '—'}</td>
                                        <td>{q.totalValue != null ? fmt(q.totalValue) : '—'}</td>
                                        <td>
                                            {q.createdAt
                                                ? new Date(q.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                                                : '—'}
                                        </td>
                                        <td>
                                            {q.viewerLogs?.length > 0
                                                ? <span className="ql-viewers-badge">{q.viewerLogs.length} viewer{q.viewerLogs.length > 1 ? 's' : ''}</span>
                                                : <span className="ql-unseen">Not viewed</span>}
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                <button
                                                    className="ql-copy-btn"
                                                    style={{ background: 'var(--primary-color)', color: 'white' }}
                                                    onClick={() => navigate(`/shared-cart?id=${q.id}`)}
                                                >
                                                    👁 View
                                                </button>
                                                <button
                                                    className="ql-copy-btn"
                                                    onClick={() => handleCopy(q.cartName, q.id)}
                                                    style={{
                                                        background: copiedId === q.id ? '#e6f4ea' : '',
                                                        color: copiedId === q.id ? '#137333' : ''
                                                    }}
                                                >
                                                    {copiedId === q.id ? '✓ Copied!' : '📋 Copy Link'}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="9" style={{textAlign: 'center', padding: '2rem', color: '#888'}}>
                                        No carts match your search.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>

                    {totalPages > 1 && (
                        <div className="ql-pagination">
                            <button
                                className="ql-page-btn"
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                            >
                                ← Prev
                            </button>
                            <div className="ql-page-numbers">
                                {Array.from({ length: totalPages }, (_, i) => i + 1)
                                    .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                                    .reduce((acc, p, idx, arr) => {
                                        if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
                                        acc.push(p);
                                        return acc;
                                    }, [])
                                    .map((p, idx) =>
                                        p === '…'
                                            ? <span key={`ellipsis-${idx}`} className="ql-page-ellipsis">…</span>
                                            : <button
                                                key={p}
                                                className={`ql-page-btn ${currentPage === p ? 'ql-page-btn--active' : ''}`}
                                                onClick={() => setCurrentPage(p)}
                                              >{p}</button>
                                    )
                                }
                            </div>
                            <button
                                className="ql-page-btn"
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                            >
                                Next →
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const AdminPage = () => {
    const { user } = useContext(AuthContext);
    const { addToast } = useToast();
    const [products, setProducts] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [refetchTrigger, setRefetchTrigger] = useState(0);

    const [isCategoryManagerVisible, setIsCategoryManagerVisible] = useState(false);
    const [isBrandManagerVisible, setIsBrandManagerVisible] = useState(false);

    const [currentPage, setCurrentPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const categoryFilter = searchParams.get('category');
    const subcategoryFilter = searchParams.get('subcategory');

    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
    const [sortOrder, setSortOrder] = useState('name,asc');

    const [priceBounds, setPriceBounds] = useState(null);
    const [priceRange, setPriceRange] = useState([0, 10000]);
    const [appliedPriceRange, setAppliedPriceRange] = useState([0, 10000]);
    const [showCuratorForm, setShowCuratorForm] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [productToDelete, setProductToDelete] = useState(null);

    // The view is driven by ?tab= so every admin screen has a shareable URL and
    // the back button works. Unknown values fall back to Products.
    const tabParam = searchParams.get('tab');
    const currentView = adminViewFromParam(tabParam);

    const [quoteSubView, setQuoteSubView] = useState('list');
    const [resumeDraftId, setResumeDraftId] = useState(null);
    const [pendingProducts, setPendingProducts] = useState([]);

    const [sidebarCollapsed, setSidebarCollapsed] = useState(
        () => typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches
    );
    const badges = useFeatureBadges(user?.role);

    // The expanded sidebar is 220px, which on a phone left an 80px slot for the
    // content area — too narrow to read the workspaces table through. Collapse
    // to the 64px icon rail below the desktop breakpoint. Labels are rendered
    // conditionally in JSX, so this has to be driven by state, not CSS alone.
    // A manual toggle still wins until the viewport next crosses the breakpoint.
    useEffect(() => {
        const mq = window.matchMedia('(max-width: 1023px)');
        const apply = (e) => setSidebarCollapsed(e.matches);
        mq.addEventListener('change', apply);
        return () => mq.removeEventListener('change', apply);
    }, []);

    useEffect(() => {
        document.body.classList.add('admin-mode');
        return () => document.body.classList.remove('admin-mode');
    }, []);

    const handleSidebarNav = (key) => {
        if (key === 'QuoteCart') setQuoteSubView('list');
        // Push the view into the URL rather than local state, so the screen can
        // be linked to and the back button steps between views.
        navigate(key === 'Products' ? '/admin' : `/admin?tab=${key}`);
    };

    const observer = useRef();
    const lastProductElementRef = useCallback(node => {
        if (isLoading) return;
        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setCurrentPage(prevPage => prevPage + 1);
            }
        });
        if (node) observer.current.observe(node);
    }, [isLoading, hasMore]);

    // FIXED: Added refetchTrigger to dependency array to refresh pending items after edit
    useEffect(() => {
        if (currentView === 'Pending') {
            api.get('/products/pending')
               .then(res => setPendingProducts(res.data))
               .catch(err => console.error("Failed to load pending products:", err));
        }
    }, [currentView, refetchTrigger]);

    const handleApprovePending = async (id) => {
        try {
            await api.put(`/products/${id}/approve`);
            setPendingProducts(prev => prev.filter(p => p.id !== id));
            addToast('Product approved. It is now live on the store.', 'success');
        } catch (e) {
            console.error(e);
            addToast('Failed to approve product.', 'error');
        }
    };

    const handleRejectPending = async (id) => {
        const feedback = window.prompt("Reason for rejection? (This will be sent to the employee):");
        if (!feedback) return;

        try {
            await api.put(`/products/${id}/reject`, { feedback });
            setPendingProducts(prev => prev.filter(p => p.id !== id));
            addToast('Product rejected and sent back to the employee.', 'success');
        } catch (e) {
            console.error(e);
            addToast('Failed to reject product.', 'error');
        }
    };

    useEffect(() => {
        const fetchPriceBounds = async () => {
            try {
                const params = {
                    search: appliedSearchTerm,
                    category: categoryFilter,
                    subcategory: subcategoryFilter
                };
                const response = await api.get('/products/price-range', { params });
                const min = Math.floor(response.data.minPrice) || 0;
                const max = Math.ceil(response.data.maxPrice) || 10000;

                setPriceBounds([min, max]);
                if (refetchTrigger > 0 || !priceBounds) {
                    setPriceRange([min, max]);
                    setAppliedPriceRange([min, max]);
                }
            } catch (err) {
                console.error("Failed to fetch price range.", err);
                setPriceBounds([0, 10000]);
                setPriceRange([0, 10000]);
                setAppliedPriceRange([0, 10000]);
            }
        };
        if (currentView === 'Products') {
            fetchPriceBounds();
        }
    }, [appliedSearchTerm, categoryFilter, subcategoryFilter, refetchTrigger, currentView]);


    useEffect(() => {
        if (!priceBounds || currentView !== 'Products') return;

        const fetchProducts = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const params = {
                    page: currentPage,
                    size: 24,
                    search: appliedSearchTerm,
                    sort: sortOrder,
                    category: categoryFilter,
                    subcategory: subcategoryFilter,
                    minPrice: appliedPriceRange[0],
                    maxPrice: appliedPriceRange[1]
                };
                const response = await api.get('/products', { params });

                setProducts(prev => currentPage === 0 ? response.data.content : [...prev, ...response.data.content]);
                setHasMore(!response.data.last);

            } catch (err) {
                setError("Failed to fetch products.");
            } finally {
                setIsLoading(false);
            }
        };
        fetchProducts();
    }, [currentPage, sortOrder, categoryFilter, subcategoryFilter, appliedPriceRange, appliedSearchTerm, priceBounds, refetchTrigger, currentView]);

    const handleCuratorSubmit = async (curatorData) => {
        try {
            const token = localStorage.getItem('jwtToken');

            if (!token) {
                addToast('Your session has expired. Please log in again.', 'error');
                navigate('/login');
                return;
            }

            if (user?.role !== 'ADMIN') {
                addToast('Only admins can create curators.', 'error');
                return;
            }

            await authService.post('/auth/admin/create', {
                ...curatorData,
                role: 'CURATOR'
            });

            addToast(`Curator ${curatorData.username || curatorData.email || ''} created.`.replace('  ', ' '), 'success');
            setShowCuratorForm(false);
        } catch (error) {
            console.error('Error creating curator:', error);
            if (error.response?.status === 403) {
                addToast('Access denied — you need to be signed in as an admin.', 'error');
            } else if (error.response?.status === 401) {
                addToast('Your session has expired. Please log in again.', 'error');
                navigate('/login');
            } else {
                addToast(error.response?.data || 'Failed to create curator.', 'error');
            }
        }
    };

    const handleDeleteProduct = (product) => {
        setProductToDelete(product);
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = async (reason) => {
        if (!productToDelete) return;
        try {
            const token = localStorage.getItem('jwtToken');
            await api.delete(`/products/${productToDelete.id}?reason=${encodeURIComponent(reason)}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setProducts(products.filter(p => p.id !== productToDelete.id));
            setPendingProducts(prev => prev.filter(p => p.id !== productToDelete.id));
            addToast(`"${productToDelete.name}" deleted.`, 'success');
        } catch (error) {
            console.error("Error deleting product:", error);
            addToast('Failed to delete product.', 'error');
        } finally {
            setIsDeleteModalOpen(false);
            setProductToDelete(null);
        }
    };

    const debouncedApplyFilters = useCallback(
        debounce((search, price) => {
            if (currentPage !== 0) setCurrentPage(0);
            setAppliedSearchTerm(search);
            setAppliedPriceRange(price);
        }, 500),
        []
    );

    const handleSliderChange = (value) => {
        setPriceRange(value);
        debouncedApplyFilters(searchTerm, value);
    };

    const handleSearchChange = (e) => {
        const newSearchTerm = e.target.value;
        setSearchTerm(newSearchTerm);
        debouncedApplyFilters(newSearchTerm, priceRange);
    };

    const refetchData = () => {
        setSearchTerm('');
        setAppliedSearchTerm('');
        setProducts([]);
        setCurrentPage(0);
        setHasMore(true);
        setRefetchTrigger(prev => prev + 1);
    };

    const handleAddOrUpdateProduct = () => {
        closeModal();
        refetchData();
    };

    const handleSortChange = (e) => {
        setSortOrder(e.target.value);
        setProducts([]);
        setCurrentPage(0);
        setHasMore(true);
    };

    const openModal = () => {
        setEditingProduct(null);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditingProduct(null);
    };

    const handleEditProduct = (product) => {
        setEditingProduct(product);
        setIsModalOpen(true);
    };

    const heading = categoryFilter
        ? `Managing Products in "${categoryFilter}"`
        : subcategoryFilter
        ? `Managing Products in "${subcategoryFilter}"`
        : 'All Products';

    const renderProductsContent = () => {
        if (products.length === 0 && isLoading) return <div className="loading-state">Loading...</div>;
        if (products.length === 0 && error) return <div className="error-state">Error: {error}</div>;

        return (
            <>
                <div className="management-widgets">
                    <CategoryManager
                        isVisible={isCategoryManagerVisible}
                        onToggle={() => setIsCategoryManagerVisible(prev => !prev)}
                    />
                    <BrandManager
                        isVisible={isBrandManagerVisible}
                        onToggle={() => setIsBrandManagerVisible(prev => !prev)}
                    />
                </div>

                <div className="product-list-section">
                    <h2 className="product-list-heading">{heading}</h2>
                    <div className="controls-bar">
                        <button onClick={openModal} className="add-product-button">
                            + Add Product
                        </button>
                        <button onClick={() => setShowCuratorForm(true)} className="add-product-button">
                            + Create Curator
                        </button>
                        <input
                            type="text"
                            placeholder="Search products..."
                            className="search-input"
                            value={searchTerm}
                            onChange={handleSearchChange}
                        />
                        <div className="price-slider-container">
                            <label className="price-label">Price Range</label>
                            <div className="price-values">
                                <span>₹{priceRange[0]}</span>
                                <span>₹{priceRange[1]}</span>
                            </div>
                            <ReactSlider
                                className="horizontal-slider"
                                thumbClassName="slider-thumb"
                                trackClassName="slider-track"
                                value={priceRange}
                                min={priceBounds ? priceBounds[0] : 0}
                                max={priceBounds ? priceBounds[1] : 10000}
                                ariaLabel={['Lower thumb', 'Upper thumb']}
                                pearling
                                minDistance={100}
                                onChange={handleSliderChange}
                            />
                        </div>
                        <div className="sorting-bar">
                            <span className="sort-label">Sort by:</span>
                            <select onChange={handleSortChange} value={sortOrder} className="sort-button">
                                <option value="name,asc">Name: A-Z</option>
                                <option value="name,desc">Name: Z-A</option>
                                <option value="mrp,asc">Price: Low to High</option>
                                <option value="mrp,desc">Price: High to Low</option>
                            </select>
                        </div>
                    </div>

                    <main className="products-grid">
                        {products.map((product, index) => {
                            const productProps = {
                                key: product.id,
                                product: product,
                                userRole: user?.role,
                                onEdit: handleEditProduct,
                                onDelete: () => handleDeleteProduct(product),
                                onApprove: user?.role === 'ADMIN' ? () => handleApprovePending(product.id) : undefined,
                                onReject: user?.role === 'ADMIN' ? () => handleRejectPending(product.id) : undefined
                            };

                            if (products.length === index + 1) {
                                return (
                                    <div ref={lastProductElementRef} key={product.id}>
                                        <ProductCard {...productProps} />
                                    </div>
                                );
                            }
                            return <ProductCard {...productProps} />;
                        })}
                    </main>

                    {isLoading && <div className="loading-state">Loading more products...</div>}

                    {!hasMore && products.length > 0 &&
                        <p style={{ textAlign: 'center', marginTop: '2rem' }}>You've reached the end of the list.</p>
                    }

                    {!isLoading && products.length === 0 &&
                        <p className="no-products-found">No products match your search.</p>
                    }
                </div>
            </>
        );
    };

    const renderContent = () => {
        switch (currentView) {
            case 'Products':
                return renderProductsContent();
            case 'Leads':
                return <LeadsManagement />;
            case 'Employee':
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 80px)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                            <div>
                                <h2 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: 0 }}>Employee View</h2>
                                <p style={{ fontSize: 12, color: '#9CA3AF', margin: '3px 0 0' }}>This is exactly what your employees see when they browse the store.</p>
                            </div>
                            <a href="/#/products?preview=employee" target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#F29213', fontWeight: 600, textDecoration: 'none' }}>Open in new tab ↗</a>
                        </div>
                        <iframe
                            src="/#/products?preview=employee"
                            title="Employee View"
                            style={{ flex: 1, border: '1.5px solid #E5E7EB', borderRadius: 10, background: '#fff' }}
                        />
                    </div>
                );
            case 'QuoteCart':
                return quoteSubView === 'create'
                    ? <QuoteCartPage
                          initialMode="create"
                          resumeDraftId={resumeDraftId}
                          onDone={() => { setResumeDraftId(null); setQuoteSubView('list'); }}
                      />
                    : <QuotesList
                          onCreateNew={() => { setResumeDraftId(null); setQuoteSubView('create'); }}
                          onResumeDraft={(id) => { setResumeDraftId(id); setQuoteSubView('create'); }}
                      />;
            case 'CartAnalytics':
                return <CartAnalytics />;
            case 'Users':
                return <UserManagement />;
            case 'TeamChats':
                return <TeamChatAdmin />;
            case 'Reports':
                return <AnalyticsReportsManagement />;
            case 'Brandstore':
                return <BrandstoreManagement />;
            case 'Gallery':
                return <GalleryManagement />;
            case 'CatalogImport':
                return <CatalogImportManagement />;
            case 'VisitBookings':
                return <VisitBookingsManagement />;
            case 'Pending':
                return (
                    <div className="product-list-section">
                        <h2 className="product-list-heading">
                            {user?.role === 'ADMIN' ? 'Review Queue' : 'My Submissions'}
                        </h2>
                        {pendingProducts.length === 0 ? (
                            <div className="empty-state">No products pending approval.</div>
                        ) : (
                            <div className="products-grid">
                                {pendingProducts.map(product => (
                                    <ProductCard
                                        key={product.id}
                                        product={product}
                                        userRole={user?.role}
                                        onEdit={handleEditProduct}
                                        onDelete={() => handleDeleteProduct(product)}
                                        onApprove={user?.role === 'ADMIN' ? () => handleApprovePending(product.id) : undefined}
                                        onReject={user?.role === 'ADMIN' ? () => handleRejectPending(product.id) : undefined}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                );
            default:
                return renderProductsContent();
        }
    };

    return (
        <div className="admin-page-container">
            <AdminSidebar
                currentView={currentView}
                onNavigate={handleSidebarNav}
                user={user}
                badges={badges}
                collapsed={sidebarCollapsed}
                onToggle={() => setSidebarCollapsed(c => !c)}
            />
            <div className="admin-content-area">
                {renderContent()}
            </div>

            {isModalOpen && (
                <AddProductModal
                    isOpen={isModalOpen}
                    onClose={closeModal}
                    onProductAdded={handleAddOrUpdateProduct}
                    productToEdit={editingProduct}
                />
            )}

            {showCuratorForm && (
                <CreateCuratorForm
                    onSubmit={handleCuratorSubmit}
                    onCancel={() => setShowCuratorForm(false)}
                />
            )}

            <DeleteConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleConfirmDelete}
                productName={productToDelete?.name}
            />
        </div>
    );
};

export default AdminPage;